import { isStateAbbreviation, STATE_NAMES } from "../constants/states";

/** First reporting year EPA requires the OSHA hazard categories everywhere. */
export const HAZARD_CATEGORIES_FEDERAL_FROM_RY = 2027;

/** States that adopted the categories before the federal year. */
export const HAZARD_CATEGORIES_STATE_FROM_RY: Readonly<Record<string, number>> = {
  MO: 2026,
};

export type InventoryAssociation = {
  state: string | null | undefined;
  reportingYear: number;
};

/** The first reporting year a facility in `state` files the hazard categories. */
export function hazardCategoriesFromReportingYear(state: string | null | undefined): number {
  const stateYear = state ? HAZARD_CATEGORIES_STATE_FROM_RY[state] : undefined;
  return stateYear ?? HAZARD_CATEGORIES_FEDERAL_FROM_RY;
}

/**
 * Whether a chemical is edited in hazard-category terms: true once any of its
 * facility associations falls in a year that facility's state requires them.
 */
export function usesHazardCategories(associations: readonly InventoryAssociation[]): boolean {
  return associations.some(
    (association) =>
      association.reportingYear >= hazardCategoriesFromReportingYear(association.state)
  );
}

/** The states, sorted, where an association falls in a year that requires the categories. */
export function statesRequiringHazardCategories(
  associations: readonly InventoryAssociation[]
): string[] {
  const states = new Set<string>();
  for (const { state, reportingYear } of associations) {
    if (state && reportingYear >= hazardCategoriesFromReportingYear(state)) states.add(state);
  }
  return [...states].sort();
}

/** State abbreviations as a readable list of names, e.g. "Missouri and Texas". */
export function stateNameList(states: readonly string[]): string {
  const names = states.map((state) => (isStateAbbreviation(state) ? STATE_NAMES[state] : state));
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}
